<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { SearchAPI } from "$lib/api/search";
  import { BillingAPI } from "$lib/api/billing";
  import { MATCH_STATUS, SEARCH_PROGRESS, SUBSCRIPTION_TIER } from "$lib/types";
  import type { SearchInfo, SearchResult } from "$lib/types";
  import { searchStore, authStore as Current } from "$lib/stores";
  import SearchView from "$lib/components/search/searchView/searchView.svelte";

  const dispatch = createEventDispatcher<{
    fetchMore: boolean;
    findMore: void;
    findAll: void;
    alert: string;
    criteriaFilterChanged: { toggles: Record<string, boolean> };
  }>();

  let {
    currentSearch,
    searchResults,
    // localSearchProgress,
    isLoadingData,
    isViewedByOwner,
    isLoadingMoreResults,
    isSearchInProgress,
    showSignInPrompt,
    subscriptionDialogOpen = $bindable(false),
  } = $props<{
    currentSearch: SearchInfo;
    searchResults: SearchResult[];
    // localSearchProgress: typeof SEARCH_PROGRESS[keyof typeof SEARCH_PROGRESS];
    isLoadingData: boolean;
    isLoadingMoreResults: boolean;
    isViewedByOwner: boolean;
    isSearchInProgress: boolean;
    subscriptionDialogOpen: boolean;
    showSignInPrompt: () => void;
  }>();

  // let filteredResults = $derived(searchResults ? filterResults(searchResults) : []);
  let filteredResults = $state<SearchResult[]>([]);
  // let criteriaToggles = $state<Record<string, boolean>>({});
  let isExportingPremiumCSV = $state(false);
  let isCsvExportDialogOpen = $state(false);
  let showDefiniteOnly = $state(false);

  // const hasMoreResultsToLoad = $derived(
  //   currentSearch?.results &&
  //   (currentSearch.definiteMatch || 0) + (currentSearch.partialMatch || 0) > currentSearch.results.length
  // );

  const allResultsDisplayed = $derived(
    getResultsDisplayed() >= (currentSearch?.definiteMatch || 0) + (currentSearch?.partialMatch || 0),
  );

  const isLoading = $derived(isLoadingData || !currentSearch?.results || currentSearch.results.length === 0);

  const isLoadingMore = $derived(isLoadingMoreResults || isSearchInProgress);

  function getResultsDisplayed() {
    const storeSearch = searchStore.getSearch(currentSearch?.slug || "");
    const storeResults = storeSearch?.results?.length || 0;
    const currentResults = currentSearch?.results?.length || 0;
    return Math.max(storeResults, currentResults);
  }

  function filterResults(results: SearchResult[]): SearchResult[] {
    if (!results.length) return [];

    let validResults = results.slice();

    if (showDefiniteOnly) {
      validResults = validResults.filter((result) => result.match === MATCH_STATUS.DEFINITE_MATCH);
    } else {
      validResults = validResults.filter(
        (result) => result.match === MATCH_STATUS.DEFINITE_MATCH || result.match === MATCH_STATUS.PARTIAL_MATCH,
      );
    }

    // for (const [criterion, toggleValue] of Object.entries(criteriaToggles)) {
    //   if (toggleValue) {
    //     validResults = validResults.filter((result) => {
    //       const criteriaMatches = result.criteriaAndReasons?.filter((cr) => cr.criteria === criterion);
    //       return criteriaMatches?.length > 0 && criteriaMatches.every((cr) => cr.isMatch === "match");
    //     });
    //   }
    // }

    return validResults.sort((a, b) => {
      const aIsDefinite = a.match === MATCH_STATUS.DEFINITE_MATCH;
      const bIsDefinite = b.match === MATCH_STATUS.DEFINITE_MATCH;
      return aIsDefinite === bIsDefinite ? 0 : aIsDefinite ? -1 : 1;
    });
  }

  // function handleCriteriaToggle(e: CustomEvent<{ toggles: Record<string, boolean> }>) {
  //   criteriaToggles = e.detail.toggles;
  //   if (currentSearch?.results) {
  //     filteredResults = filterResults(searchResults);
  //   }
  //   dispatch('criteriaFilterChanged', { toggles: criteriaToggles });
  // }

  const handleCSVDownload = async (searchId?: string | number, userId?: string | number) => {
    if ($Current.isLoggedIn && $Current.user?.plan !== SUBSCRIPTION_TIER.FREE) {
      try {
        await SearchAPI.downloadCSV(
          searchId || currentSearch.id,
          userId || $Current.user?.id || 0,
          currentSearch?.query,
        );
      } catch (error) {
        console.error("Failed to download CSV:", error);
        alert("Failed to download CSV. Please try again.");
      }
    } else {
      isCsvExportDialogOpen = false;
      subscriptionDialogOpen = true;
    }
  };

  let csvPollingTimer: NodeJS.Timeout | null = null;

  function startCSVPolling() {
    logger.log("Starting CSV polling...");
    stopCSVPolling();

    if (!currentSearch?.id) {
      logger.log("No search ID available, cannot start polling");
      return;
    }

    csvPollingTimer = setInterval(() => {
      checkCSVDownloadStatus();
    }, 2500);
    checkCSVDownloadStatus();
  }

  function stopCSVPolling() {
    if (csvPollingTimer) {
      clearInterval(csvPollingTimer);
      csvPollingTimer = null;
    }
  }

  async function checkCSVDownloadStatus() {
    if (!currentSearch?.id) {
      logger.log("No search ID available for CSV status check");
      return;
    }
    logger.log("Checking CSV download status...");
    try {
      const status = await SearchAPI.checkCSVDownloadWithEmailStatus(currentSearch?.id);
      if (status) {
        logger.log("CSV status:", status);
        isExportingPremiumCSV = status === "processing" || status === "created";
        // isPremiumReady = status === "completed";

        if (status !== "processing" && status !== "created") {
          logger.log("CSV processing complete, stopping polling");
          stopCSVPolling();
          if (currentSearch?.id) {
            SearchAPI.completeCSVDownloadWithEmail(currentSearch.id, currentSearch.query);
          }
          Current.updateUserCredits();
        }
      }
    } catch (error) {
      console.error("Error checking CSV download status:", error);
      stopCSVPolling();
    }
  }

  async function handlePremiumCSVDownload(searchId: string | number, userId: string | number) {
    try {
      isExportingPremiumCSV = false;

      const canPerformAction = await BillingAPI.checkCreditsAvailability(
        "search_email_export",
        (currentSearch?.partialMatch || 0) + (currentSearch?.definiteMatch || 0),
      );

      if (!canPerformAction) {
        dispatch("alert", "You don't have enough credits to perform this action");
        isCsvExportDialogOpen = false;
        return;
      }

      const result = await SearchAPI.startCSVDownloadWithEmail(searchId, userId);
      if (result) {
        isExportingPremiumCSV = true;
        startCSVPolling();
      }
    } catch (error) {
      console.error("Failed to download premium CSV:", error);
      throw error;
    }
  }

  function handleFetchMore() {
    dispatch("fetchMore", showDefiniteOnly);
  }

  function handleFindMore() {
    dispatch("findMore");
  }

  function handleFindAll() {
    dispatch("findAll");
  }

  $effect(() => {
    if (currentSearch?.results) {
      filteredResults = filterResults(searchResults);
    }
  });
</script>

<SearchView
  bind:showDefiniteOnly
  bind:subscriptionDialogOpen
  type={currentSearch?.category === "company" ? "company" : "person"}
  recordType={"search"}
  queryToShow={currentSearch.query}
  definiteMatchesCount={currentSearch.definiteMatch || 0}
  partialMatchesCount={currentSearch.partialMatch || 0}
  isLoadingData={isLoading}
  {isSearchInProgress}
  displayedResults={filteredResults}
  {currentSearch}
  {isViewedByOwner}
  {isExportingPremiumCSV}
  {showSignInPrompt}
  {allResultsDisplayed}
  {isLoadingMoreResults}
  on:fetchMore={handleFetchMore}
  on:findMore={handleFindMore}
  on:findAll={handleFindAll}
  on:csvDownload={(e) => handleCSVDownload()}
  on:premiumCsvDownload={(e) => handlePremiumCSVDownload(e.detail.searchId, e.detail.userId)}
/>
